import ReportLead from './ReportLead';
import { Box, Card, Chip, LinearProgress, Skeleton, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    getExecutiveGlassHeroSx,
    getExecutiveGlassKpiSx,
    getExecutiveGlassPillSx,
    getReportHeroCardSx,
    getReportMetricTileSx
} from './reportSurfaceStyles';
import { formatUsd } from '../../system/report-formatters';

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatCurrency = (value) => formatUsd(Number(value || 0));
const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const formatRatio = (value) => `${Number(value || 0).toFixed(2)}x`;

const resolveMetricValue = (value) => {
    if (value && typeof value === 'object' && value.count !== undefined) {
        return value.count;
    }
    return value;
};

const renderMetricValue = (value, format) => {
    switch (format) {
        case 'currency':
            return formatCurrency(value);
        case 'percent':
            return formatPercent(value);
        case 'ratio':
            return formatRatio(value);
        default:
            return formatNumber(value);
    }
};

export default function ReportOverviewHero(props) {
    const {
        eyebrow,
        title,
        subtitle,
        chips = [],
        metrics = [],
        loading = false,
        loadingLabel = 'Refreshing report data',
        variant = 'default'
    } = props;

    const theme = useTheme();
    const isExecutive = variant === 'executive';
    const heroCardSx = useMemo(() => (
        isExecutive ? getExecutiveGlassHeroSx(theme) : getReportHeroCardSx(theme)
    ), [isExecutive, theme]);
    const metricTileSx = useMemo(() => (
        isExecutive ? getExecutiveGlassKpiSx(theme) : getReportMetricTileSx(theme)
    ), [isExecutive, theme]);
    const pillSx = useMemo(() => getExecutiveGlassPillSx(theme), [theme]);
    const visibleChips = chips.filter((chip) => chip && chip.value !== undefined && chip.value !== null && String(chip.value).trim() !== '');
    const metricItems = metrics.length
        ? metrics
        : Array.from({ length: 8 }).map((_, index) => ({ label: `metric-${index}`, loading: true }));

    return (
        <Card elevation={0} sx={heroCardSx}>
            <Box sx={{ position: 'relative', p: { xs: 2, md: 2.5 }, display: 'grid', gap: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        gap: isExecutive ? 2 : 1.5
                    }}
                >
                    <ReportLead eyebrow={eyebrow} title={title} description={subtitle} />

                    {visibleChips.length ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                alignItems: 'flex-start',
                                justifyContent: { xs: 'flex-start', md: 'flex-end' }
                            }}
                        >
                            {visibleChips.map((chip) => (
                                isExecutive ? (
                                    <Box key={`${chip.label}-${chip.value}`} sx={pillSx}>
                                        <Typography variant="caption" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
                                            {chip.label}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                            {chip.value}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Chip
                                        key={`${chip.label}-${chip.value}`}
                                        label={`${chip.label}: ${chip.value}`}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.72)' }}
                                    />
                                )
                            ))}
                        </Box>
                    ) : null}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'grid', gap: 0.75 }}>
                        <LinearProgress />
                        <Typography variant="caption" color="text.secondary">
                            {loadingLabel}
                        </Typography>
                    </Box>
                ) : null}

                <Box
                    sx={{
                        display: 'grid',
                        gap: 1,
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            md: 'repeat(4, minmax(0, 1fr))',
                        }
                    }}
                >
                    {metricItems.map((metric, index) => {
                        if (loading && !metrics.length) {
                            return (
                                <Box key={`hero-skel-${index}`} sx={metricTileSx}>
                                    <Skeleton variant="text" width="40%" />
                                    <Skeleton variant="text" width="65%" height={38} />
                                    <Skeleton variant="text" width="55%" />
                                </Box>
                            );
                        }

                        const value = resolveMetricValue(metric.value);
                        return (
                            <Box key={metric.label || index} sx={metricTileSx}>
                                <Typography variant="overline" sx={{ letterSpacing: '0.1em', color: 'text.secondary' }}>
                                    {metric.label}
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.7rem', md: '2rem' } }}>
                                    {renderMetricValue(value, metric.format)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {metric.helper || 'Selected report range'}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Card>
    );
}
